import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { MainCurrencyDto } from '../_core/_model/currency';
import { ApiService } from '../_core/_services/api.service';
import { AuthService } from '../_core/_services/auth.service';
import { FormatService } from '../_core/_services/format';
import { Router } from '@angular/router';
import { OrderDraftService, OrderPrefill } from '../_core/_services/order-draft-service';
import { NotificationService } from '../_core/_services/notification.service';
import { CreateOrder, OrderDto } from '../_core/_model/order';
import { WalletDetailsDto } from '../_core/_model/wallet';
import { UserDto } from '../_core/_model/users';
import { ApiMeta } from '../_core/_model/api-response';
import { Modal } from 'bootstrap';
import { WalletTierData } from '../_core/_model/compliance';
import { HouseTradeService } from '../_core/_services/house-trade.service';
import { HouseTradeQuoteDto, TradeExecutionMode } from '../_core/_model/house-trade';
import { OrderEntry, OrderEntrySubmission } from '../common/widget/order-entry/order-entry';

@Component({
  selector: 'app-orders',
  standalone: false,
  templateUrl: './orders.html',
  styleUrl: './orders.css',
})
export class Orders implements OnInit {
  loading: boolean = false;
  loadingWallet: boolean = false;
  errorObject: { error: boolean, message: string, showButton?: boolean, buttonText?: string, buttonRoute?: string } | null = null;

  currencies: MainCurrencyDto[] = [];

  orderForm!: FormGroup;
  searchForm!: FormGroup;

  fee: number = 0.0;
  available: number = 0;
  remaining: number = 0;

  tradeMode: TradeExecutionMode = 'instant';
  instantQuote: HouseTradeQuoteDto | null = null;


  // add near other fields
  reviewOpen = false;            // optional, useful if you want to disable background stuff
  private reviewModal?: Modal;     // Bootstrap modal instance

  pendingDraft: OrderPrefill | null = null;
  @ViewChild(OrderEntry) orderEntry?: OrderEntry;
  private selectedHaveCurrency = '';

  private readonly amountPattern = /^(?:\d+|\d*\.\d{1,2})$/;

  showSearchResult: boolean = false;

  loadingAllOrders: boolean = true;
  loadingOpenOrders: boolean = true;
  loadingPartialOrders: boolean = true;
  loadingCompletedOrders: boolean = true;
  loadingCancelledOrders: boolean = true;
  loadingFailedOrders: boolean = true;
  loadingSearchResults: boolean = false;

  allOrdersPage: number = 1;
  openOrdersPage: number = 1;
  partialOrdersPage: number = 1;
  completedOrdersPage: number = 1;
  cancelledOrdersPage: number = 1;
  failedOrdersPage: number = 1;
  searchResultsPage: number = 1;

  allOrders: OrderDto[] = [];
  openOrders: OrderDto[] = [];
  partialOrders: OrderDto[] = [];
  completedOrders: OrderDto[] = [];
  cancelledOrders: OrderDto[] = [];
  failedOrders: OrderDto[] = [];
  searchResults: OrderDto[] = [];

  allOrdersMeta!: ApiMeta;
  openOrdersMeta!: ApiMeta;
  partialOrdersMeta!: ApiMeta;
  completedOrdersMeta!: ApiMeta;
  cancelledOrdersMeta!: ApiMeta;
  failedOrdersMeta!: ApiMeta;
  searchResultsMeta!: ApiMeta;

  userData!: UserDto;
  walletId!: string;
  wallet: WalletDetailsDto | null = null;

  activeTab: string = 'tabAll';
  url: any;

  pageBlocker = '';
  pageBlockerCode = '';

  constructor(
    private fb: FormBuilder,
    private api: ApiService,
    private format: FormatService,
    private auth: AuthService,
    private router: Router,
    private draft: OrderDraftService,
    private notifyService: NotificationService,
    private houseTradeService: HouseTradeService,
  ) {
    this.orderForm = this.fb.nonNullable.group({
      haveCurrency: ['', Validators.required],
      haveAmount: [0, [Validators.required, Validators.min(0.01), Validators.pattern(this.amountPattern)]],
      needCurrency: ['', Validators.required],
      needAmount: [0, [Validators.required, Validators.min(0.01), Validators.pattern(this.amountPattern)]],
      price: [0, [Validators.required, Validators.min(0.00000000001), Validators.pattern(/^(?:\d+|\d*\.\d{1,12})$/)]]
    });
    this.searchForm = this.fb.nonNullable.group({
      searchQuery: ['', Validators.required],
    });

    this.url = this.router.url.slice(1);

  }

  get haveCurrency() { return this.orderForm.get('haveCurrency'); }
  get haveAmount() { return this.orderForm.get('haveAmount'); }
  get needCurrency() { return this.orderForm.get('needCurrency'); }
  get needAmount() { return this.orderForm.get('needAmount'); }
  get price() { return this.orderForm.get('price'); }

  get isInstantMode(): boolean {
    return this.tradeMode === 'instant';
  }

  get hasUsableInstantQuote(): boolean {
    return !!this.instantQuote?.quoteId &&
      this.instantQuote.instantAvailable &&
      !this.isInstantQuoteExpired;
  }

  get isInstantQuoteExpired(): boolean {
    const expiresAt = this.instantQuote?.expiresAt;
    return !!expiresAt && new Date(expiresAt).getTime() <= Date.now();
  }

  get searchQuery() { return this.searchForm.get('searchQuery'); }

  refresh() {
    this.loading = true;
    this.loadMain();
    this.loadOrders();
    this.loadOrdersByStatus('Open');
    this.loadOrdersByStatus('Partial');
    this.loadOrdersByStatus('Completed');
    this.loadOrdersByStatus('Cancelled');
    this.loadOrdersByStatus('Failed');
    this.loading = false;
  }

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe();
    this.pendingDraft = this.draft.get();
    this.loadProfile();

    this.loadMain();
    this.loadOrders();
    this.loadOrdersByStatus('Open');
    this.loadOrdersByStatus('Partial');
    this.loadOrdersByStatus('Completed');
    this.loadOrdersByStatus('Cancelled');
    this.loadOrdersByStatus('Failed');
  }


  refreshInstantQuote(): void {
    this.orderEntry?.refreshInstantQuote();
  }

  getWalletFromCurrency(currency: string): void {
    this.selectedHaveCurrency = currency;
    this.loading = true;

    if (!currency) {
      this.available = 0;
      this.wallet = null;
      this.configureHaveAmountValidators();
      this.loading = false;
      return;
    }

    if (!this.userData?.wallets) {
      this.available = 0;
      this.configureHaveAmountValidators();
      this.loading = false;
      return;
    }

    const wallet = this.userData?.wallets?.find(w => w.currency.code === currency);

    if (wallet) {
      this.walletId = wallet.walletId;
      this.available = wallet.availableBalance;
      this.configureHaveAmountValidators();
      this.loadWallet();

      const haveAmount = Number(this.haveAmount?.value ?? 0);
      const totalSpend = haveAmount + (haveAmount * this.fee / 100);

      if (wallet.availableBalance >= totalSpend) {
        this.errorObject = null;
      } else {
        this.notifyService.showError(`Insufficient balance for currency ${currency}`, "Wallet Error");
        this.errorObject = {
          error: true,
          message: `Insufficient balance for currency ${currency}. Please ensure you have enough funds before placing an order.`,
          showButton: true,
          buttonText: `Topup ${currency}`,
          buttonRoute: `/wallets/deposit/${wallet.walletId}?amount=${totalSpend}&returnUrl=/orders`
        };
      }
    } else {
      this.available = 0;
      this.wallet = null;
      this.remaining = 0;
      this.configureHaveAmountValidators();
      this.notifyService.showError(`No wallet found for currency ${currency}`, "Wallet Error");
      this.errorObject = {
        error: true,
        message: `No wallet found for currency ${currency}. Please create a wallet for this currency before placing an order`,
        showButton: true,
        buttonText: `Create ${currency} Wallet`,
        buttonRoute: `/wallets/create?currency=${currency}`
      };
    }

    this.loading = false;
  }

  loadWallet() {
    this.loadingWallet = true;

    this.api.getSingleWallet(this.walletId).subscribe({
      next: (res) => {
        this.wallet = res?.data ?? null;
        this.remaining = this.wallet?.currencyRules?.usage?.remainingOrder ?? 0;
        this.configureHaveAmountValidators();
      },
      error: (e) => {
        const error = this.getApiErrorMessage(e, 'Failed to load wallet');

        this.applyBlockerFromError(e);
        this.notifyService.showError(error, 'Wallet Error');

        this.wallet = null;
        this.loadingWallet = false;
      },
      complete: () => {
        this.loadingWallet = false;
      },
    });
  }

  routeErrorButton() {
    this.router.navigateByUrl(this.errorObject?.buttonRoute ?? '');
  }

  loadMain() {
    this.loading = true;

    this.api.getMain().subscribe({
      next: (res) => {
        this.currencies = (res.data?.currencies ?? []).filter(c => c.status === 'Active');
      },
      error: (e) => (this.errorObject = {
        error: true,
        message: e?.error?.Error?.Details ?? 'Failed to load main data'
      }),
      complete: () => (this.loading = false),
    });
  }


  private loadProfile(): void {
    this.api.getProfile().subscribe({
      next: (res) => {
        this.userData = res.data;

        const currency = this.selectedHaveCurrency || this.pendingDraft?.haveCurrency || '';
        if (currency) {
          this.getWalletFromCurrency(currency);
        }
      },
      error: (e) => {
        const message = e?.error?.Error?.Details ?? 'Failed to load profile';
        this.notifyService.showError(message, 'Profile Error');
      }
    });
  }

  private configureHaveAmountValidators(): void {
    const minOrder = this.wallet?.currencyRules?.minOrder ?? 0.01;
    const maxSpendable = this.getMaxSpendableHaveAmount();
    const validators = [
      Validators.required,
      Validators.min(minOrder),
      Validators.pattern(this.amountPattern)
    ];

    if (maxSpendable > 0 || this.wallet || this.userData?.wallets) {
      validators.push(Validators.max(maxSpendable));
    }

    this.haveAmount!.setValidators(validators);
    this.haveAmount!.updateValueAndValidity({ emitEvent: false });
  }

  get maximumSpendableAmount(): number | null {
    if (!this.userData?.wallets || !this.selectedHaveCurrency) return null;
    return this.getMaxSpendableHaveAmount();
  }

  private getMaxSpendableHaveAmount(): number {
    const available = Number(this.available || 0);
    const maxByAvailableBalance = available > 0 ? available / (1 + (this.fee / 100)) : 0;

    if (!this.wallet) {
      return maxByAvailableBalance;
    }

    const orderLimits = this.getPositiveOrderLimits();
    if (!orderLimits.length) {
      return maxByAvailableBalance;
    }

    return Math.min(maxByAvailableBalance, ...orderLimits);
  }

  private getPositiveOrderLimits(): number[] {
    if (!this.wallet) {
      return [];
    }

    const limits: number[] = [];

    if (this.remaining > 0) {
      limits.push(this.remaining);
    }

    const tradeLimit = this.wallet.tierInfo?.limits?.find(
      limit => limit.activityType.toLowerCase() === 'trade'
    );

    if (tradeLimit && !tradeLimit.isUnlimited) {
      [
        tradeLimit.perTransactionLimit,
        tradeLimit.remainingDailyLimit,
        tradeLimit.remainingMonthlyLimit
      ].forEach(value => {
        const numericValue = Number(value ?? 0);
        if (numericValue > 0) {
          limits.push(numericValue);
        }
      });
    }

    return limits;
  }

  getOrder(status: string | null, page: number) {
    this.setPage(status!, page);
    if (status === null) this.loadOrders();
    else this.loadOrdersByStatus(status);
  }

  loadOrders() {
    this.loadingAllOrders = true;
    this.errorObject = null;

    this.activeTab = 'tabAll';

    this.showSearchResult = false;
    this.loadingSearchResults = false;

    this.api.getUserOrders(this.getPage(null)).subscribe({
      next: (res) => {
        const orders = res?.data ?? [];
        this.allOrders = orders;
        const meta: ApiMeta | any = res?.meta;
        this.setOrdersMeta(null, meta);
      },
      error: (e) => {
        this.errorObject = {
          error: true,
          message: e?.error?.Error?.Details ?? 'Failed to load orders'
        };
        this.notifyService.showError(this.errorObject.message, "Error");
      },
      complete: () => (this.loadingAllOrders = false),
    });
  }

  loadOrdersByStatus(status: string) {
    this.errorObject = null;
    this.setLoading(status, true);

    this.activeTab = 'tabAll';

    this.showSearchResult = false;
    this.loadingSearchResults = false;

    this.api.getUserOrdersByStatus(status, this.getPage(status)).pipe(
      finalize(() => {
        this.setLoading(status, false);
      })
    ).subscribe({
      next: (res) => {
        const orders = res?.data ?? [];
        const meta: ApiMeta | any = res.meta;
        this.setOrders(status, orders);
        this.setOrdersMeta(status, meta);
      },
      error: (e) => {
        const msg = e?.error?.Error?.Details ?? `Failed to load ${status} orders`;
        this.notifyService.showError(msg, `${status} Error`);
      },
      complete: () => (this.setLoading(status, false)),
    });
  }

  private setLoading(status: string | null, isLoading: boolean): void {
    if (status === 'Open') this.loadingOpenOrders = isLoading;
    else if (status === 'Partial') this.loadingPartialOrders = isLoading;
    else if (status === 'Completed') this.loadingCompletedOrders = isLoading;
    else if (status === 'Cancelled') this.loadingCancelledOrders = isLoading;
    else if (status === 'Failed') this.loadingFailedOrders = isLoading;
    else this.loadingAllOrders = isLoading; // if you have it
  }

  private setOrders(status: string | null, orders: OrderDto[]): void {
    if (status === 'Open') this.openOrders = orders;
    else if (status === 'Partial') this.partialOrders = orders;
    else if (status === 'Completed') this.completedOrders = orders;
    else if (status === 'Cancelled') this.cancelledOrders = orders;
    else if (status === 'Failed') this.failedOrders = orders;
    else this.allOrders = orders;
  }

  private setOrdersMeta(status: string | null, meta: ApiMeta): void {
    if (status === 'Open') this.openOrdersMeta = meta;
    else if (status === 'Partial') this.partialOrdersMeta = meta;
    else if (status === 'Completed') this.completedOrdersMeta = meta;
    else if (status === 'Cancelled') this.cancelledOrdersMeta = meta;
    else if (status === 'Failed') this.failedOrdersMeta = meta;
    else this.allOrdersMeta = meta;
  }

  private getPage(status: string | null): number {
    if (status === 'Open') return this.openOrdersPage;
    else if (status === 'Partial') return this.partialOrdersPage;
    else if (status === 'Completed') return this.completedOrdersPage;
    else if (status === 'Cancelled') return this.cancelledOrdersPage;
    else if (status === 'Failed') return this.failedOrdersPage;
    else return this.allOrdersPage;
  }

  private setPage(status: string, page: number): void {
    if (status === 'Open') this.openOrdersPage = page;
    else if (status === 'Partial') this.partialOrdersPage = page;
    else if (status === 'Completed') this.completedOrdersPage = page;
    else if (status === 'Cancelled') this.cancelledOrdersPage = page;
    else if (status === 'Failed') this.failedOrdersPage = page;
    else this.allOrdersPage = page;
  }

  handleOrderEntrySubmit(submission: OrderEntrySubmission): void {
    this.tradeMode = submission.mode;
    this.instantQuote = submission.quote;

    this.orderForm.patchValue({
      haveCurrency: submission.draft.haveCurrency,
      haveAmount: submission.draft.haveAmount,
      needCurrency: submission.draft.needCurrency,
      needAmount: submission.draft.needAmount,
      price: submission.draft.price
    }, { emitEvent: false });
    this.orderForm.updateValueAndValidity({ emitEvent: false });

    this.selectedHaveCurrency = submission.draft.haveCurrency;
    this.pendingDraft = null;
    this.openReviewModal();
  }

  submitOrder() {
    if (this.isInstantMode) {
      this.submitInstantTrade();
      return;
    }

    if (this.loading || this.pageBlocker) return;

    this.submittedOrTouchAll();

    if (this.orderForm.invalid || this.errorObject?.error) {
      this.loading = false;
      return;
    }

    const { haveCurrency, haveAmount, needCurrency, needAmount, price } = this.orderForm.getRawValue();

    const order: CreateOrder = {
      fromCurrency: haveCurrency,
      toCurrency: needCurrency,
      amount: haveAmount,
      rate: price,
    };

    this.loading = true;

    this.api.createOrder(order).subscribe({
      next: (res) => {
        if (res.success) {
          this.draft.pop();
          this.auth.getCurrentUser().subscribe();
          this.notifyService.showSuccess('Order created successfully!', 'Orders');
          this.router.navigate(['/orders/manage', res.data.id, 'done']);
        } else {
          this.notifyService.showError(res.message ?? 'Failed to create order', 'Error');
          this.loading = false;
        }
      },
      error: (e) => {
        const msg = this.getApiErrorMessage(e, 'Failed to create order');

        this.applyBlockerFromError(e);
        this.notifyService.showError(msg, 'Error');

        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }


  private submitInstantTrade(): void {
    if (this.loading || this.pageBlocker) return;

    this.submittedOrTouchAll();

    if (!this.hasUsableInstantQuote || !this.instantQuote?.quoteId) {
      this.closeReviewModal();
      this.orderEntry?.refreshInstantQuoteAndSubmit();
      return;
    }

    const quoteId = this.instantQuote.quoteId;
    this.loading = true;

    this.houseTradeService.executeInstantTrade({ quoteId }).subscribe({
      next: (res) => {
        if (!res.success || !res.data?.customerOrderId) {
          this.notifyService.showError(res.message ?? 'Failed to execute instant trade', 'Instant Trade');
          return;
        }

        this.draft.pop();
        this.instantQuote = null;
        this.orderEntry?.clearInstantQuote();
        this.auth.getCurrentUser().subscribe();
        this.notifyService.showSuccess(
          res.data.status === 'Matched'
            ? 'Instant trade matched successfully.'
            : 'Instant trade created. The remaining amount is being processed.',
          'Instant Trade'
        );

        this.router.navigate(['/orders/manage', res.data.customerOrderId, 'done']);
      },
      error: (e) => {
        const code = this.getApiErrorCode(e);
        const message = this.getApiErrorMessage(e, 'Failed to execute instant trade');

        this.applyBlockerFromError(e);
        this.notifyService.showError(message, 'Instant Trade');

        if (
          code === 'QUOTE_EXPIRED' ||
          code === 'HOUSE_QUOTE_EXPIRED' ||
          code === 'HOUSE_LIQUIDITY_UNAVAILABLE' ||
          code === 'INSUFFICIENT_HOUSE_LIQUIDITY'
        ) {
          this.closeReviewModal();
          this.orderEntry?.refreshInstantQuoteAndSubmit();
        }
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  submitSearch() {
    if (this.searchForm.invalid) return;

    const query = this.searchQuery?.value.trim();
    if (!query) return;

    this.activeTab = 'tabSearch';

    this.showSearchResult = true;
    this.loadingSearchResults = true;
    this.errorObject = null;

    this.api.searchUserOrdersByStatus(query, this.searchResultsPage).pipe(
      finalize(() => {
        this.loadingSearchResults = false;
      })
    ).subscribe({
      next: (res) => {
        const orders = res?.data ?? [];
        if (orders.length > 0) {
          this.searchResults = orders;
          this.notifyService.showSuccess(`Found ${orders.length} order(s) matching "${query}"`, "Search Results");
        } else {
          this.notifyService.showInfo(`No orders found matching "${query}"`, "Search Results");
        }
      },
      error: (e) => {
        const msg = e?.error?.Error?.Details ?? 'Failed to search orders';
        this.notifyService.showError(msg, "Error");
      }
    });
  }

  getOrderStatusClass(status: string): string {
    return this.format.getOrderStatusClass(status);
  }

  viewOrder(orderId: string) {
    this.router.navigate(['/orders/manage', orderId]);
  }

  cancelOrder(orderId: string) {
    if (!confirm('Are you sure you want to cancel this order?')) return;

    this.api.cancelOrder(orderId).subscribe({
      next: (res) => {
        if (res.success) {
          this.notifyService.showSuccess('Order cancelled successfully', "Orders");
          this.refresh();
        } else {
          this.notifyService.showError(res.message ?? 'Failed to cancel order', "Error");
        }
      },
      error: (e) => {
        const msg = e?.error?.Error?.Details ?? 'Failed to cancel order';
        this.notifyService.showError(msg, "Error");
      }
    });
  }

  shortId(id: string, start = 6, end = 4): string {
    if (!id) return '';
    if (id.length <= start + end) return id;

    return `${id.slice(0, start)}…${id.slice(-end)}`;
  }

  copyToClipboard(text: string) {
    if (!text) return;

    navigator.clipboard.writeText(text).then(() => {
      this.notifyService?.showSuccess?.('Order ID copied', 'Copied');
    }).catch(() => {
      this.notifyService?.showError?.('Failed to copy Order ID', 'Error');
    });
  }

  confirmPlaceOrder() {
    if (!this.canPlaceOrder) return;

    this.closeReviewModal();
    this.submitOrder();
  }

  private ensureModal() {
    const w = window as any;
    if (!this.reviewModal) {
      const el = document.getElementById('orderReviewModal');
      if (!el) return;
      this.reviewModal = new w.bootstrap.Modal(el, { backdrop: 'static', keyboard: false });
    }
  }

  openReviewModal() {
    this.submittedOrTouchAll();

    if (this.isInstantMode && this.isInstantQuoteExpired) {
      this.orderEntry?.refreshInstantQuoteAndSubmit();
      return;
    }

    // block opening if invalid or there is a blocking error
    if (!this.canPlaceOrder) return;

    // this.getWalletFromCurrency(this.haveCurrency?.value);

    this.ensureModal();
    if (!this.reviewModal) return;

    this.reviewOpen = true;
    this.reviewModal.show();
  }

  closeReviewModal() {
    if (!this.reviewModal) return;
    this.reviewOpen = false;
    this.reviewModal.hide();
  }


  getLimitData(tierInfo: WalletTierData | null, activityType: string, key: "remainingDailyLimit" | "remainingMonthlyLimit" | "perTransactionLimit" | "dailyLimit" | "monthlyLimit") {
    return this.remaining + this.format.getLimitData(tierInfo, activityType, key);
  }

  checkLimit(activityType: string) {
    return (this.remaining > 0) || this.format.checkLimit(this.haveAmount?.value, this.wallet!, activityType);
  }

  private getApiErrorCode(e: any): string {
    return e?.error?.error?.code || e?.error?.Error?.Code || '';
  }

  private getApiErrorMessage(e: any, fallback: string): string {
    return e?.error?.error?.details ||
      e?.error?.Error?.Details ||
      e?.error?.message ||
      fallback;
  }

  private applyBlockerFromError(e: any): void {
    const code = this.getApiErrorCode(e);
    const message = this.getApiErrorMessage(e, 'Orders are temporarily unavailable.');

    if (
      code === 'COMPLIANCE_BLOCK' ||
      code === 'COUNTRY_CHANGE_COOLDOWN' ||
      code === 'KYC_REQUIRED' ||
      code === 'LIMIT_EXCEEDED'
    ) {
      this.pageBlockerCode = code;
      this.pageBlocker = message;

      this.errorObject = {
        error: true,
        message
      };
    }
  }

  get canPlaceOrder(): boolean {
    const baseValid = !this.pageBlocker &&
      !this.loading &&
      !this.loadingWallet &&
      !this.errorObject?.error &&
      this.orderForm.valid;

    if (!baseValid) return false;
    if (!this.isInstantMode) return true;

    return this.hasUsableInstantQuote;
  }

  /** mark controls touched so validation shows before modal */
  private submittedOrTouchAll() {
    Object.values(this.orderForm.controls).forEach(c => c.markAsTouched());
    this.orderForm.updateValueAndValidity({ emitEvent: false });
  }
}
